import React, { useState, useRef } from 'react';
import {
  createUserWithEmailAndPassword,
  sendEmailVerification,
} from 'firebase/auth';
import { getStorage, ref, uploadBytes, getDownloadURL } from 'firebase/storage';
import { doc, setDoc } from 'firebase/firestore';
import { useNavigate } from 'react-router-dom';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { LocalizationProvider, DatePicker } from '@mui/x-date-pickers';
import {
  Box,
  Button,
  TextField,
  Select,
  MenuItem,
  FormControl,
  InputLabel,
  CircularProgress,
  Typography,
  Card,
  Input,
  FormHelperText,
  Grid,
  InputAdornment,
  IconButton,
  Container,
  Divider,
  Checkbox,
  FormControlLabel,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
// Icons
import PersonOutlineIcon from '@mui/icons-material/PersonOutline';
import EmailOutlinedIcon from '@mui/icons-material/EmailOutlined';
import LockOutlinedIcon from '@mui/icons-material/LockOutlined';
import PhoneOutlinedIcon from '@mui/icons-material/PhoneOutlined';
import CheckCircleIcon from '@mui/icons-material/CheckCircleOutlined';
import HomeOutlinedIcon from '@mui/icons-material/HomeOutlined';
import CalendarTodayOutlinedIcon from '@mui/icons-material/CalendarTodayOutlined';
import BadgeIcon from '@mui/icons-material/Badge';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import CloudUploadIcon from '@mui/icons-material/CloudUpload';
import CardMembershipOutlinedIcon from '@mui/icons-material/CardMembershipOutlined';
import CloseIcon from '@mui/icons-material/Close';
import { auth, db } from '../firebase';
import logo from '../assets/bouldou white.png';

// ── Constants & Styles ───────────────────────────────────────────────────────
const primaryColor = '#B200BB';
const secondaryColor = '#FF3B30';
const customFontFamily = 'Poppins, Arial, sans-serif';

const styles = {
  pageWrapper: {
    minHeight: '100vh',
    position: 'relative',
    display: 'flex',
    alignItems: 'center',
    justifyContent: 'center',
    padding: { xs: '16px', },
  },
  gradientBg: {
    position: 'absolute',
    top: 0, left: 0, right: 0, bottom: 0,
    background: `linear-gradient(135deg, ${primaryColor} 0%, ${secondaryColor} 100%)`,
    zIndex: -1,
  },
  logoContainer: { textAlign: 'center', mb: 4 },
  card: {
    borderRadius: '24px',
    p: { xs: 3.5, sm: 5 },
    background: '#fff',
    boxShadow: '0 25px 70px rgba(0,0,0,0.22)',
    fontFamily: customFontFamily,
    mx: 'auto',
  },
  title: { fontWeight: 800, textAlign: 'center', fontSize: { xs: '2rem', sm: '2.6rem' }, color: '#000' },
  subtitle: { textAlign: 'center', color: '#666', mt: 1, fontSize: '1.05rem' },
  input: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px',
      fontSize: '1rem',
      height: '56px',
      fontFamily: customFontFamily,
    },
  },
  selectControl: {
    '& .MuiOutlinedInput-root': {
      borderRadius: '14px',
      height: '56px',
      minWidth: '220px%',
    },

  },
  icon: { color: '#aaa', fontSize: '20px' },
  strengthContainer: { display: 'flex', alignItems: 'center', mt: 1, mb: 2 },
  strengthBar: { height: 7, borderRadius: 4, transition: 'all 0.4s ease', mr: 1.5 },
  strengthLabel: { fontSize: '0.9rem', color: '#555', fontWeight: 600 },
  uploadBtn: {
    border: `2.5px dashed ${primaryColor}`,
    color: primaryColor,
    borderRadius: '16px',
    py: 2,
    px: 4,
    textTransform: 'none',
    fontWeight: 600,
    fontSize: { xs: '0.9rem', sm: '1rem' },
    '&:hover': { borderColor: primaryColor, bgcolor: `${primaryColor}08` },
  },
  previewImg: {
    width: 130,
    height: 130,
    borderRadius: '18px',
    objectFit: 'cover',
    border: `5px solid ${primaryColor}`,
    boxShadow: '0 8px 25px rgba(178,0,187,0.3)',
  },
  submitBtn: {
    mt: 3,
    py: 2,
    borderRadius: '16px',
    background: `linear-gradient(45deg, ${primaryColor}, ${secondaryColor})`,
    fontSize: '1.2rem',
    fontWeight: 700,
    textTransform: 'none',
    boxShadow: '0 10px 30px rgba(178, 0, 187, 0.4)',
    '&:hover': { boxShadow: '0 15px 40px rgba(178, 0, 187, 0.5)', transform: 'translateY(-2px)' },
  },
  linkText: { color: '#666', fontSize: '1rem', mt: 3 },
  link: { color: primaryColor, fontWeight: 'bold', cursor: 'pointer', '&:hover': { textDecoration: 'underline' } },
};

// ── Password Strength ────────────────────────────────────────────────────────
const getPasswordStrength = (p) => {
  let strength = 0;
  if (p.length >= 8) strength += 1;
  if (/[A-Z]/.test(p)) strength += 1;
  if (/[a-z]/.test(p)) strength += 1;
  if (/[0-9]/.test(p)) strength += 1;
  if (/[\W_]/.test(p)) strength += 1;
  if (p.length === 0) return { label: '', width: '0%', color: 'transparent' };
  if (strength <= 1) return { label: 'Weak', width: '30%', color: '#ff5252' };
  if (strength === 2) return { label: 'Fair', width: '55%', color: '#ff9800' };
  if (strength === 3) return { label: 'Good', width: '80%', color: '#ffb300' };
  return { label: 'Strong', width: '100%', color: '#4caf50' };
};

// ── Main Component ───────────────────────────────────────────────────────────
const SignUp = () => {
  const [firstName, setFirstName] = useState('');
  const [lastName, setLastName] = useState('');
  const [nin, setNin] = useState('');
  const [ninnum, setNinNum] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [sex, setSex] = useState('');
  const [dob, setDob] = useState(null);
  const [phoneNumber, setPhoneNumber] = useState('');
  const [address, setAddress] = useState('');
  const [profilePhoto, setProfilePhoto] = useState(null);
  const [termsAccepted, setTermsAccepted] = useState(false);
  const [error, setError] = useState('');
  const [passwordStrength, setPasswordStrength] = useState(getPasswordStrength(''));
  const [isLoading, setIsLoading] = useState(false);
  const [showPassword, setShowPassword] = useState(false);
  const [showConfirmPassword, setShowConfirmPassword] = useState(false);

  // Terms dialog states
  const [openTerms, setOpenTerms] = useState(false);
  const [hasReadTerms, setHasReadTerms] = useState(false);
  const termsRef = useRef(null);

  const navigate = useNavigate();

  const handlePasswordChange = (value) => {
    setPassword(value);
    setPasswordStrength(getPasswordStrength(value));
  };

  const pickImage = (e) => {
    const file = e.target.files[0];
    if (file) setProfilePhoto(file);
  };

  // Modified scroll handler to be more strict
  const handleScroll = (e) => {
    const { scrollTop, scrollHeight, clientHeight } = e.target;
    // If user reaches within 50px of the bottom
    if (scrollHeight - scrollTop <= clientHeight + 50) {
      setHasReadTerms(true);
    }
  };

  const handleCheckboxClick = (e) => {
    if (!hasReadTerms) {
      e.preventDefault(); // Stop the check
      setOpenTerms(true); // Force open the popup
    }
  };

  // ── Helper: Calculate age from DOB ────────────────────────────────────────
  const calculateAge = (birthDate) => {
    if (!birthDate) return null;
    const today = new Date();
    let age = today.getFullYear() - birthDate.getFullYear();
    const m = today.getMonth() - birthDate.getMonth();
    if (m < 0 || (m === 0 && today.getDate() < birthDate.getDate())) {
      age--;
    }
    return age;
  };

  const handleSignUp = async () => {
    setError('');

    // 1. Validation Checks
    if (!firstName || !lastName || !nin || !ninnum || !email || !password || !confirmPassword || !sex || !dob || !phoneNumber || !address) {
      return setError('Please fill in all required fields.');
    }

    if (password !== confirmPassword) {
      return setError('Passwords do not match.');
    }

    if (!termsAccepted) {
      return setError('Please read and accept the Terms and Conditions.');
    }

    const age = calculateAge(dob);
    if (age === null || age < 18) {
      return setError('You must be at least 18 years old to create an account.');
    }

    setIsLoading(true);

    try {
      // 2. Create User
      const userCredential = await createUserWithEmailAndPassword(auth, email, password);
      const user = userCredential.user;

      // 3. Send Verification Email
      await sendEmailVerification(user);

      // 4. Upload Files (NIN and Profile Photo)
      let photoUrl = null;
      let ninUrl = null;
      const storage = getStorage();

      if (profilePhoto) {
        const photoRef = ref(storage, `profilePhotos/${user.uid}.jpg`);
        await uploadBytes(photoRef, profilePhoto);
        photoUrl = await getDownloadURL(photoRef);
      }

      if (nin) {
        const ninRef = ref(storage, `nins/${user.uid}`);
        await uploadBytes(ninRef, nin);
        ninUrl = await getDownloadURL(ninRef);
      }

      // 5. Store User Data in Firestore
      await setDoc(doc(db, 'users', user.uid), {
        firstName,
        lastName,
        ninnum,
        ninUrl, // Added storage link for the NIN file
        email,
        sex,
        dob: dob.toISOString().split('T')[0],
        phoneNumber,
        address,
        profilePhoto: photoUrl || null,
        createdAt: new Date().toISOString(),
        isActive: true,
        status: 'pending',
        reportsCount: 0,
        ageAtSignup: age,
      });

      // 6. Sign out & Redirect
      // We sign out because createUserWithEmailAndPassword signs them in automatically.
      // Signing out forces them to go through your Login logic which checks 'emailVerified'.
      await auth.signOut();

      alert('Account created! A verification link has been sent to your email. Please verify your email before signing in.');
      navigate('/signin');

    } catch (err) {
      console.error(err);
      const msg =
        err.code === 'auth/email-already-in-use'
          ? 'This email is already in use.'
          : err.code === 'auth/weak-password'
            ? 'Password is too weak.'
            : err.message || 'An error occurred. Please try again.';
      setError(msg);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <Box sx={styles.pageWrapper}>
      <Box sx={styles.gradientBg} />
      <Container maxWidth="sm">
        <Box sx={styles.logoContainer}>
          <img src={logo} alt="Bouldou" style={{ height: '50px' }} />
        </Box>

        <Card sx={styles.card} elevation={10}>
          <Typography variant="h4" sx={styles.title}>
            Create Your Account
          </Typography>
          <Typography variant="body1" sx={styles.subtitle}>
            Join Bouldou and start connecting today
          </Typography>

          <Grid container spacing={2.5} sx={{ mt: 3, mx: 'auto' }}>
            {/* First & Last Name */}
            <Grid item xs={12} sm={6} sx={{ mx: 'auto' }}>
              <TextField
                label="First Name"
                value={firstName}
                onChange={(e) => setFirstName(e.target.value)}
                fullWidth
                required
                sx={styles.input}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineIcon sx={styles.icon} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mx: 'auto' }}>
              <TextField
                label="Last Name"
                value={lastName}
                onChange={(e) => setLastName(e.target.value)}
                fullWidth
                required
                sx={styles.input}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PersonOutlineIcon sx={styles.icon} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>


            <Grid item xs={12} sx={{ mx: 'auto' }}>
              <TextField
                label="Email Address"
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                fullWidth
                required
                sx={styles.input}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <EmailOutlinedIcon sx={styles.icon} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Grid item xs={12} sm={6} sx={{ mx: 'auto' }}>
              <TextField
                label="NIN"
                placeholder="000-0000-0-0-00"
                value={ninnum}
                onChange={(e) => setNinNum(e.target.value)}
                fullWidth
                required
                sx={styles.input}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <CardMembershipOutlinedIcon sx={styles.icon} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* Phone & Gender */}
            <Grid item xs={12} sm={6} sx={{ mx: 'auto' }}>
              <TextField
                label="Phone Number"
                placeholder="+248 5xxx xxxx"
                value={phoneNumber}
                onChange={(e) => setPhoneNumber(e.target.value)}
                fullWidth
                required
                sx={styles.input}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <PhoneOutlinedIcon sx={styles.icon} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mx: 'auto', }}>
              <FormControl fullWidth sx={styles.selectControl} style={{ minWidth: '220px' }}>
                <InputLabel>Gender</InputLabel>
                <Select value={sex} onChange={(e) => setSex(e.target.value)} label="Gender" required>
                  <MenuItem value=""><em>Select gender</em></MenuItem>
                  <MenuItem value="Male">Male</MenuItem>
                  <MenuItem value="Female">Female</MenuItem>
                </Select>
              </FormControl>
            </Grid>
            <Grid item xs={12} sm={6} sx={{ mx: 'auto' }}>
              <TextField
                label="Address"
                value={address}
                onChange={(e) => setAddress(e.target.value)}
                fullWidth
                required
                sx={styles.input}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <HomeOutlinedIcon sx={styles.icon} />
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            {/* DOB & Address */}
            <Grid item xs={12} sm={6} sx={{ mx: 'auto', maxWidth: { sm: '220px' } }}>
              <LocalizationProvider dateAdapter={AdapterDateFns}>
                <DatePicker
                  label="Date of Birth"
                  value={dob}
                  onChange={setDob}
                  format="dd/MM/yyyy"
                  maxDate={new Date()}
                  slotProps={{ textField: { required: true } }}
                  sx={{ ...styles.input, '& .MuiOutlinedInput-root': { height: '56px', borderRadius: '14px' } }}
                  style={{ maxWidth: '220px' }}
                  slots={{
                    openPickerIcon: () => <CalendarTodayOutlinedIcon sx={{ ...styles.icon, ml: 1 }} />,
                  }}
                />
              </LocalizationProvider>
            </Grid>

          </Grid>

          <Grid item xs={12} sx={{ my: 3, textAlign: 'center', mx: 'auto' }}>
            <Typography variant="body2" color="text.secondary" sx={{ my: 1, ml: 1, fontWeight: 600 }}>
              Upload National ID (NIN) Photo *
            </Typography>
            <Button
              component="label"
              variant="outlined"
              fullWidth
              startIcon={nin ? <CheckCircleIcon sx={{ color: '#2ecc71' }} /> : <BadgeIcon />}
              sx={{
                py: 3,
                borderStyle: 'dashed',
                borderRadius: '14px',
                textTransform: 'none',
                borderColor: nin ? '#2ecc71' : '#ccc',
                color: nin ? '#27ae60' : 'text.secondary',
                bgcolor: nin ? 'rgba(46, 204, 113, 0.05)' : 'transparent',
                '&:hover': {
                  borderColor: '#C00068',
                  bgcolor: 'rgba(192, 0, 104, 0.04)',
                },
              }}
            >
              {nin ? `ID Uploaded: ${nin.name}` : "Click to upload a photo of your NIN card"}
              <input
                type="file"
                hidden
                accept="image/*,application/pdf"
                onChange={(e) => setNin(e.target.files[0])}
              />
            </Button>
            {nin && (
              <Typography variant="caption" sx={{ mt: 1, display: 'block', textAlign: 'center', color: '#666' }}>
                Tap again to replace the current file
              </Typography>
            )}
          </Grid>
          <Divider sx={{ my: 5 }} />

          <Grid container>
            {/* Password */}
            <Grid item xs={12} md={12} sx={{ mx: 'auto', minWidth: '300px' }}>
              <TextField
                label="Create Password"
                type={showPassword ? 'text' : 'password'}
                value={password}
                onChange={(e) => handlePasswordChange(e.target.value)}
                fullWidth
                required
                sx={styles.input}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={styles.icon} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowPassword(!showPassword)}>
                        {showPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
              <Box sx={styles.strengthContainer}>
                <Box sx={{ ...styles.strengthBar, width: passwordStrength.width, bgcolor: passwordStrength.color }} />
                {passwordStrength.label && (
                  <Typography variant="caption" sx={styles.strengthLabel}>
                    Password Strength: <strong>{passwordStrength.label}</strong>
                  </Typography>
                )}
              </Box>
            </Grid>

            <Grid item xs={12} md={12} sx={{ mx: 'auto', minWidth: '300px' }}>
              <TextField
                label="Confirm Password"
                type={showConfirmPassword ? 'text' : 'password'}
                value={confirmPassword}
                onChange={(e) => setConfirmPassword(e.target.value)}
                fullWidth
                required
                sx={styles.input}
                InputProps={{
                  startAdornment: (
                    <InputAdornment position="start">
                      <LockOutlinedIcon sx={styles.icon} />
                    </InputAdornment>
                  ),
                  endAdornment: (
                    <InputAdornment position="end">
                      <IconButton onClick={() => setShowConfirmPassword(!showConfirmPassword)}>
                        {showConfirmPassword ? <VisibilityOff /> : <Visibility />}
                      </IconButton>
                    </InputAdornment>
                  ),
                }}
              />
            </Grid>

            <Divider sx={{ my: 5 }} />
            {/* Profile Photo */}
            <Grid item xs={12} textAlign="center">
              <Button variant="outlined" component="label" startIcon={<CloudUploadIcon />} sx={styles.uploadBtn}>
                {profilePhoto ? 'Change Profile Photo' : 'Upload Profile Photo (Optional)'}
                <Input type="file" accept="image/*" onChange={pickImage} sx={{ display: 'none' }} />
              </Button>
              {profilePhoto && (
                <Box sx={{ mt: 3 }}>
                  <img src={URL.createObjectURL(profilePhoto)} alt="Preview" style={styles.previewImg} />
                  <Typography variant="caption" display="block" sx={{ mt: 1, color: '#666' }}>
                    {profilePhoto.name}
                  </Typography>
                </Box>
              )}
            </Grid>

            {/* Terms & Conditions Checkbox */}
            <Grid item xs={12}>
              <FormControlLabel
                control={
                  <Checkbox
                    checked={termsAccepted}
                    onChange={(e) => setTermsAccepted(e.target.checked)}
                    // INTERCEPTION: If not read, trigger popup
                    onClick={handleCheckboxClick}
                    sx={{
                      color: primaryColor,
                      '&.Mui-checked': { color: primaryColor },
                    }}
                  />
                }
                label={
                  <Typography variant="body2" sx={{ fontFamily: customFontFamily, color: '#444' }}>
                    I agree to the{' '}
                    <Box component="span" sx={{ color: primaryColor, fontWeight: 600, textDecoration: 'underline', cursor: 'pointer' }} onClick={() => setOpenTerms(true)}>
                      Terms and Conditions
                    </Box>
                  </Typography>
                }
              />
              {!hasReadTerms && (
                <FormHelperText sx={{ ml: '14px', color: '#d32f2f', fontWeight: 600 }}>
                  ⚠ You must open and scroll through the Terms to accept.
                </FormHelperText>
              )}
            </Grid>

            {/* Error Message */}
            {error && (
              <Grid item xs={12}>
                <FormHelperText error sx={{ textAlign: 'center', fontSize: '1rem', fontWeight: 500 }}>
                  {error}
                </FormHelperText>
              </Grid>
            )}

            {/* Submit Button */}
            <Grid item xs={12}>
              <Button
                variant="contained"
                onClick={handleSignUp}
                disabled={isLoading || !termsAccepted}
                fullWidth
                sx={styles.submitBtn}
              >
                {isLoading ? <CircularProgress size={28} color="inherit" /> : 'Create Account'}
              </Button>
            </Grid>

            {/* Links */}
            <Grid item xs={12} textAlign="center">
              <Typography sx={styles.linkText}>
                Already have an account?{' '}
                <Box component="span" onClick={() => navigate('/signin')} sx={styles.link}>
                  Sign In
                </Box>
              </Typography>
              <Typography sx={{ ...styles.linkText, mt: 1.5 }}>
                <Box component="span" onClick={() => navigate('/')} sx={styles.link}>
                  Back to Home
                </Box>
              </Typography>
            </Grid>
          </Grid>
        </Card>
      </Container>

      {/* Terms & Conditions + Privacy Policy Dialog */}
      <Dialog
        open={openTerms}
        onClose={() => setOpenTerms(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle sx={{
          fontWeight: 700,
          fontFamily: customFontFamily,
          borderBottom: '1px solid #eee',
          pb: 1.5,
          pr: 6,
        }}>
          Terms of Use & Privacy Policy
          <IconButton
            onClick={() => setOpenTerms(false)}
            sx={{ position: 'absolute', right: 12, top: 12 }}
          >
            <CloseIcon />
          </IconButton>
        </DialogTitle>

        <DialogContent
          dividers
          sx={{
            p: { xs: 3, md: 4 },
            maxHeight: '65vh',
            overflowY: 'auto',
            fontFamily: customFontFamily,
            lineHeight: 1.7,
            fontSize: '0.96rem',
            color: '#333',
          }}
          ref={termsRef}
          onScroll={handleScroll}
        >
          {/* TERMS OF USE */}
          <Typography variant="h5" gutterBottom sx={{ mt: 2, fontWeight: 700, color: primaryColor }}>
            Terms of Use
          </Typography>
          <Typography variant="caption" color="text.secondary" paragraph>
            Last revised on 08/01/2020
          </Typography>

          <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
            {`Welcome to Bouldou. If you live in the European Union, these Terms of Use are between you and:

Bouldou.sc
La Gogue
Anse Etoile
Mahe
Seychelles
info@bouldou.sc

1. Acceptance of Terms of Use Agreement.
The wording “Bouldou” which appears in this document also refers to Bouldou.sc which is the name of the dating website. By creating a Bouldou account... (continues)`}
          </Typography>

          {/* Full Terms content - truncated here for readability, but you should paste the complete text */}
          <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-wrap', mt: 3 }}>
            {`1. Acceptance of Terms of Use Agreement.
The wording “Bouldou” which appears in this document also refers to Bouldou.sc which is the name of the dating website. By creating a Bouldou account, whether through a mobile device, mobile application or computer (collectively, the “Service”) you agree to be bound by (i) these Terms of Use, (ii) our Privacy Policy, Cookie Policy and Safety Tips, each of which is incorporated by reference into this Agreement, and (iii) any terms disclosed and agreed to by you if you purchase additional features, products or services we offer on the Service (collectively, this "Agreement"). If you do not accept and agree to be bound by all of the terms of this Agreement, please do not use the Service.
We may make changes to this Agreement and to the Service from time to time. We may do this for a variety of reasons including to reflect changes in or requirements of a law, new features, or changes in business practices. The most recent version of this Agreement will be posted on the Service under Settings on www.bouldou.sc, and you should regularly check for the most recent version. The most recent version is the version that applies. If the changes include material changes that affect your rights or obligations, we will notify you in advance of the changes by reasonable means, which could include notification through the Service or via email. If you continue to use the Service after the changes become effective, then you agree to the revised Agreement.
2. Eligibility.
You must be at least 18 years of age to create an account on Bouldou.sc and use the Service. By creating an account and using the Service, you are lawfully abiding to the terms and conditions being:
•	you can form a binding contract with Bouldou,
•	you are not a person who is barred from using the Service under the laws of the Seychelles or any other similar prohibitions.
•	you will comply with this Agreement and all applicable local, state, national and international laws, rules and regulations, and
•	you have never been convicted of a felony or indictable offense (or crime of similar severity), a sex crime, or any crime involving violence, and that you are not required to register as a sex offender with any state, federal or local sex offender registry.

3. Your Account.
In order to use Bouldou, you may sign in using your Facebook login.  If you do so, you authorize us to access and use certain Facebook account information, including but not limited to your public Facebook profile. For more information regarding the information we collect from you and how we use it, please consult our Privacy Policy.
You are responsible for maintaining the confidentiality of your login credentials you use to sign up for Bouldou, and you are solely responsible for all activities that occur under those credentials. If you think someone has gained access to your account, please immediately contact us.

4. Modifying the Service and Termination.
Bouldou is always striving to improve the Service and bring you additional functionality that you will find engaging and useful. This means we may add new product features or enhancements from time to time as well as remove some features, and if these actions do not materially affect your rights or obligations, we may not provide you with notice before taking them. We may even suspend the Service entirely, in which event we will notify you in advance unless extenuating circumstances, such as safety or security concerns, prevent us from doing so.
You may terminate your account at any time, for any reason, by following the instructions in "Settings" in the Service, however if you use a third party payment account, you will need to manage in app purchases through such account (e.g., iTunes, Google Play) to avoid additional billing. Tinder may terminate your account at any time without notice if it believes that you have violated this Agreement. Upon such termination, you will not be entitled to any refund for purchases. After your account is terminated, this Agreement will terminate, except that the following provisions will still apply to you and Tinder: Section 4, Section 5, and Sections 12 through 19.

5. Safety; Your Interactions with Other Members.
Though Tinder strives to encourage a respectful member experience through features like the double opt-in that allows members to communicate only after they have both indicated interest in one another, it is not responsible for the conduct of any member on or off of the Service. You agree to use caution in all interactions with other members, particularly if you decide to communicate off the Service or meet in person. In addition, you agree to review and follow Tinder’s Safety Tips prior to using the Service. You agree that you will not provide your financial information (for example, your credit card or bank account information), or wire or otherwise send money, to other members.
YOU ARE SOLELY RESPONSIBLE FOR YOUR INTERACTIONS WITH OTHER MEMBERS. YOU UNDERSTAND THAT TINDER DOES NOT CONDUCT CRIMINAL BACKGROUND CHECKS ON ITS MEMBERS OR OTHERWISE INQUIRE INTO THE BACKGROUND OF ITS MEMBERS. TINDER MAKES NO REPRESENTATIONS OR WARRANTIES AS TO THE CONDUCT OF MEMBERS.

6. Rights Tinder Grants You.
Tinder grants you a personal, worldwide, royalty-free, non-assignable, nonexclusive, revocable, and non-sublicensable license to access and use the Service. This license is for the sole purpose of letting you use and enjoy the Service’s benefits as intended by Tinder and permitted by this Agreement. Therefore, you agree not to:
•	use the Service or any content contained in the Service for any commercial purposes without our written consent.
•	copy, modify, transmit, create any derivative works from, make use of, or reproduce in any way any copyrighted material, images, trademarks, trade names, service marks, or other intellectual property, content or proprietary information accessible through the Service without Tinder’s prior written consent.
•	express or imply that any statements you make are endorsed by Tinder.
•	use any robot, bot, spider, crawler, scraper, site search/retrieval application, proxy or other manual or automatic device, method or process to access, retrieve, index, "data mine", or in any way reproduce or circumvent the navigational structure or presentation of the Service or its contents.
•	use the Service in any way that could interfere with, disrupt or negatively affect the Service or the servers or networks connected to the Service.
•	upload viruses or other malicious code or otherwise compromise the security of the Service.
•	forge headers or otherwise manipulate identifiers in order to disguise the origin of any information transmitted to or through the Service.
•	"frame" or "mirror" any part of the Service without Tinder’s prior written authorization.
•	use meta tags or code or other devices containing any reference to Tinder or the Service (or any trademark, trade name, service mark, logo or slogan of Tinder) to direct any person to any other website for any purpose.
•	modify, adapt, sublicense, translate, sell, reverse engineer, decipher, decompile or otherwise disassemble any portion of the Service, or cause others to do so.
•	use or develop any third-party applications that interact with the Service or other members' Content or information without our written consent.
•	use, access, or publish the Tinder application programming interface without our written consent.
•	probe, scan or test the vulnerability of our Service or any system or network.
•	encourage or promote any activity that violates this Agreement.
The Company may investigate and take any available legal action in response to illegal and/ or unauthorized uses of the Service, including termination of your account.
Any software that we provide you may automatically download and install upgrades, updates, or other new features. You may be able to adjust these automatic downloads through your device's settings.

7. Rights you Grant Tinder.
By creating an account, you grant to Tinder a worldwide, transferable, sub-licensable, royalty-free, right and license to host, store, use, copy, display, reproduce, adapt, edit, publish, modify and distribute information you authorize us to access from Facebook, as well as any information you post, upload, display or otherwise make available (collectively, "post") on the Service or transmit to other members (collectively, "Content"). Tinder's license to your Content shall be non-exclusive, except that Tinder's license shall be exclusive with respect to derivative works created through use of the Service. For example, Tinder would have an exclusive license to screenshots of the Service that include your Content. In addition, so that Tinder can prevent the use of your Content outside of the Service, you authorize Tinder to act on your behalf with respect to infringing uses of your Content taken from the Service by other members or third parties. This expressly includes the authority, but not the obligation, to send notices pursuant to 17 U.S.C. § 512(c)(3) (i.e., DMCA Takedown Notices) on your behalf if your Content is taken and used by third parties outside of the Service. Our license to your Content is subject to your rights under applicable law (for example laws regarding personal data protection to the extent any Content contains personal information as defined by those laws) and is for the limited purpose of operating, developing, providing, and improving the Service and researching and developing new ones. You agree that any Content you place or that you authorize us to place on the Service may be viewed by other members and may be viewed by any person visiting or participating in the Service (such as individuals who may receive shared Content from other Tinder members).
You agree that all information that you submit upon creation of your account, including information submitted from your Facebook account, is accurate and truthful and you have the right to post the Content on the Service and grant the license to Tinder above.
You understand and agree that we may monitor or review any Content you post as part of a Service. We may delete any Content, in whole or in part, that in our sole judgment violates this Agreement or may harm the reputation of the Service.
When communicating with our customer care representatives, you agree to be respectful and kind. If we feel that your behavior towards any of our customer care representatives or other employees is at any time threatening or offensive, we reserve the right to immediately terminate your account.
In consideration for Tinder allowing you to use the Service, you agree that we, our affiliates, and our third-party partners may place advertising on the Service. By submitting suggestions or feedback to Tinder regarding our Service, you agree that Tinder may use and share such feedback for any purpose without compensating you.
Please be informed that Tinder may access, store and disclose your account information and Content if required to do so by law, by performing its agreement with you, or in a good faith belief that such access, storage or disclosure satisfies a legitimate interest, including to: (i) comply with legal process; (ii) enforce the Agreement; (iii) respond to claims that any Content violates the rights of third parties; (iv) respond to your requests for customer service; or (v) protect the rights, property or personal safety of the Company or any other person.

8. Community Rules.
By using the Service, you agree that you will not:
•	use the Service for any purpose that is illegal or prohibited by this Agreement.
•	use the Service for any harmful or nefarious purpose
•	use the Service in order to damage Tinder
•	violate our Community Guidelines, as updated from time to time.
•	spam, solicit money from or defraud any members.
•	impersonate any person or entity or post any images of another person without his or her permission.
•	bully, "stalk", intimidate, assault, harass, mistreat or defame any person.
•	post any Content that violates or infringes anyone's rights, including rights of publicity, privacy, copyright, trademark or other intellectual property or contract right.
•	post any Content that is hate speech, threatening, sexually explicit or pornographic.
•	post any Content that incites violence; or contains nudity or graphic or gratuitous violence.
•	post any Content that promotes racism, bigotry, hatred or physical harm of any kind against any group or individual.
•	solicit passwords for any purpose, or personal identifying information for commercial or unlawful purposes from other members or disseminate another person's personal information without his or her permission.
•	use another member's account, share an account with another member, or maintain more than one account.
•	create another account if we have already terminated your account, unless you have our permission.
Tinder reserves the right to investigate and/ or terminate your account without a refund of any purchases if you have violated this Agreement, misused the Service or behaved in a way that Tinder regards as inappropriate or unlawful, including actions or communications that occur on or off the Service.

9. Other Members' Content.
Although Tinder reserves the right to review and remove Content that violates this Agreement, such Content is the sole responsibility of the member who posts it, and Tinder cannot guarantee that all Content will comply with this Agreement. If you see Content on the Service that violates this Agreement, please report it within the Service or via our contact form.

10. Purchases.
Generally. From time to time, Tinder may offer products and services for purchase ("in app purchases") through iTunes, Google Play, carrier billing, Tinder direct billing or other payment platforms authorized by Tinder. If you choose to make an in app purchase, you will be prompted to confirm your purchase with the applicable payment provider, and your method of payment (be it your card or a third party account such as Google Play or iTunes) (your "Payment Method") will be charged for the in app purchase at the prices displayed to you for the service(s) you've selected as well as any sales or similar taxes that may be imposed on your payments, and you authorize Tinder or the third party account, as applicable, to charge you. If you purchase an auto-recurring periodic subscription through an in app purchase, your Payment Method will continue to be billed for the subscription until you cancel. After your initial subscription commitment period, and again after any subsequent subscription period, your subscription will automatically continue for an additional equivalent period, at the price you agreed to when subscribing.
Auto-Renewal; Automatic Card Payment
Subscriptions are automatically renewed until you terminate or cancel the subscription. When you purchase a subscription, your Payment Method will continue to be billed monthly in advance within 24 hours of the date of the initial purchase at the price you agreed to when initially subscribing. Your card payment information will be stored and subsequently used for the automatic card payments in accordance with the Agreement.
Objections to a payment already made should be directed to Customer support if you were billed directly by Tinder or the relevant third party account such as iTunes. You are also be able to object by contacting your bank or payment provider, who can provide further information on your rights as well as applicable time limits.
You may unconditionally withdraw your consent to automatic card payments at any time by going to Settings on Tinder or the relevant third party account, but be advised that you are still obligated to pay any outstanding amounts.
If you want to change or terminate your subscription, you will need to log in to your third party account (or Settings on Tinder) and follow instructions to terminate or cancel your subscription, even if you have otherwise deleted your account with us or if you have deleted the Tinder application from your device. Deleting your account on Tinder or deleting the Tinder application from your device does not terminate or cancel your subscription; Tinder will retain all funds charged to your Payment Method until you terminate or cancel your subscription on Tinder or the third party account, as applicable. If you terminate or cancel your subscription, you may use your subscription until the end of your then-current subscription term, and your subscription will not be renewed after your then-current term expires.
Additional Terms that apply if you pay Tinder directly with your Payment Method. If you pay Tinder directly, Tinder may correct any billing errors or mistakes that it makes even if it has already requested or received payment. If you initiate a chargeback or otherwise reverse a payment made with your Payment Method, Tinder may terminate your account immediately in its sole discretion.
You may edit your Payment Method information by visiting Tinder and going to Settings. If a payment is not successfully settled, due to expiration, insufficient funds, or otherwise, and you do not edit your Payment Method information, terminate or cancel your subscription, you remain responsible for any uncollected amounts and authorize us to continue billing the Payment Method, as it may be updated. This may result in a change to your payment billing dates. In addition, you authorize us to obtain updated or replacement expiration dates and card numbers for your credit or debit card as provided by your credit or debit card issuer. The terms of your payment will be based on your Payment Method and may be determined by agreements between you and the financial institution, credit card issuer or other provider of your chosen Payment Method. If you reside outside of the Americas, you agree that your payment to Tinder will be through MTCH Technology Services Limited.
Super Likes and Other Virtual Items. From time to time, you may be able to purchase a limited, personal, non-transferable, non-sublicensable, revocable license to use "virtual items", including but not limited to Super Likes (collectively, "Virtual Items"). Any Virtual Item balance shown in your account does not constitute a real-world balance or reflect any stored value, but instead constitutes a measurement of the extent of your license. Virtual Items do not incur fees for non-use, however, the license granted to you in Virtual Items will terminate in accordance with the terms of this Agreement, when Tinder ceases providing the Service or your account is otherwise closed or terminated. Tinder, in its sole discretion, reserves the right to charge fees for the right to access or use Virtual Items and/ or may distribute Virtual Items with or without charge. Tinder may manage, regulate, control, modify or eliminate Virtual Items at any time. Tinder shall have no liability to you or any third party in the event that Tinder exercises any such rights. Virtual Items may only be redeemed through the Service. ALL PURCHASES AND REDEMPTIONS OF VIRTUAL ITEMS MADE THROUGH THE SERVICE ARE FINAL AND NON-REFUNDABLE. The provision of Virtual Items for use in the Service is a service that commences immediately upon the acceptance of your purchase of such Virtual Items. YOU ACKNOWLEDGE THAT TINDER IS NOT REQUIRED TO PROVIDE A REFUND IN RESPECT OF VIRTUAL ITEMS FOR ANY REASON, AND THAT YOU WILL NOT RECEIVE MONEY OR OTHER COMPENSATION FOR UNUSED VIRTUAL ITEMS WHEN AN ACCOUNT IS CLOSED, WHETHER SUCH CLOSURE WAS VOLUNTARY OR INVOLUNTARY.
Refunds. Generally, all charges for purchases are nonrefundable, and there are no refunds or credits for partially used periods. We may make an exception if a refund for a subscription offering is requested within fourteen days of the transaction date, or if the laws applicable in your jurisdiction provide for refunds.
For subscribers residing in the EU or European Economic Area, in accordance with local law, you are entitled to a full refund without stating the reason during the 14 days after the subscription begins. Please note that this 14-day period commences when the subscription starts.
Purchases of Virtual Items are FINAL AND NON-REFUNDABLE.
To request a refund:
If you subscribed using your Apple ID, refunds are handled by Apple, not Tinder. To request a refund, go to iTunes, click on your Apple ID, select "Purchase history", find the transaction and hit "Report Problem". You can also submit a request at https://getsupport.apple.com.
If you subscribed using your Google Play Store account or through Tinder directly: please contact customer support with your order number for the Google Play Store (you can find the order number in the order confirmation email or by logging in to Google Wallet) or Tinder (you can find this on your confirmation email). You may also mail or deliver a signed and dated notice which states that you, the buyer, are canceling this agreement, or words of similar effect. Please also include the email address or mobile number associated with your account along with your order number.
If you use your right of cancellation (except for purchases made through your Apple ID, which Apple controls), we will refund (or ask Google to refund) all payments received from you, without undue delay and in any case within 14 days of the date when we received notice of your decision to cancel the Agreement. We shall make such refund using the same means of payment as used by you in the initial transaction. In any case, no fees will be charged to you as a result of the refund.
You cannot cancel an order for delivery of digital content that is not delivered on a physical medium if order processing has begun with your explicit prior consent and acknowledgement that you will thereby lose your right of cancellation. This applies, e.g., to purchases of Virtual Items. That means that such purchases are FINAL AND NON-REFUNDABLE.
Pricing. Tinder operates a global business and provides services to a diverse community of members. Our pricing structure may vary by region, length of subscription, bundle size, recent in-app promotions and other factors. We frequently test new features and price points to provide members with increased functionality and payment options should they choose to use them.

11. Notice and Procedure for Making Claims of Copyright Infringement.
If you believe that your work has been copied and posted on the Service in a way that constitutes copyright infringement, please provide our Copyright Agent with the following information:
•	an electronic or physical signature of the person authorized to act on behalf of the owner of the copyright interest;
•	a description of the copyrighted work that you claim has been infringed;
•	a description of where the material that you claim is infringing is located on the Service (and such description must be reasonably sufficient to enable us to find the alleged infringing material);
•	your contact information, including address, telephone number and email address;
•	a written statement by you that you have a good faith belief that the disputed use is not authorized by the copyright owner, its agent, or the law; and
•	a statement by you, made under penalty of perjury, that the above information in your notice is accurate and that you are the copyright owner or authorized to act on the copyright owner's behalf.
Notice of claims of copyright infringement should be provided to the Company's Copyright Agent via email to copyright@match.com, by phone to (+1) 214-576-3272 or via mail to the following address:
Copyright Compliance Department c/o Match Group Legal 8750 N. Central Expressway, Suite 1400 Dallas, Texas 75231
Tinder will terminate the accounts of repeat infringers.

12. Disclaimers.
TINDER PROVIDES THE SERVICE ON AN “AS IS” AND “AS AVAILABLE” BASIS AND TO THE EXTENT PERMITTED BY APPLICABLE LAW, GRANTS NO WARRANTIES OF ANY KIND, WHETHER EXPRESS, IMPLIED, STATUTORY OR OTHERWISE WITH RESPECT TO THE SERVICE (INCLUDING ALL CONTENT CONTAINED THEREIN), INCLUDING, WITHOUT LIMITATION, ANY IMPLIED WARRANTIES OF SATISFACTORY QUALITY, MERCHANTABILITY, FITNESS FOR A PARTICULAR PURPOSE OR NON-INFRINGEMENT. TINDER DOES NOT REPRESENT OR WARRANT THAT (A) THE SERVICE WILL BE UNINTERRUPTED, SECURE OR ERROR FREE, (B) ANY DEFECTS OR ERRORS IN THE SERVICE WILL BE CORRECTED, OR (C) THAT ANY CONTENT OR INFORMATION YOU OBTAIN ON OR THROUGH THE SERVICE WILL BE ACCURATE.
TINDER TAKES NO RESPONSIBILITY FOR ANY CONTENT THAT YOU OR ANOTHER MEMBER OR THIRD PARTY POSTS, SENDS OR RECEIVES THROUGH THE SERVICE. ANY MATERIAL DOWNLOADED OR OTHERWISE OBTAINED THROUGH THE USE OF THE SERVICE IS ACCESSED AT YOUR OWN DISCRETION AND RISK.

13. Third Party Services.
The Service may contain advertisements and promotions offered by third parties and links to other web sites or resources. Tinder is not responsible for the availability (or lack of availability) of such external websites or resources. If you choose to interact with the third parties made available through our Service, such party's terms will govern their relationship with you. Tinder is not responsible or liable for such third parties' terms or actions.

14. Limitation of Liability.
TO THE FULLEST EXTENT PERMITTED BY APPLICABLE LAW, IN NO EVENT WILL TINDER, ITS AFFILIATES, EMPLOYEES, LICENSORS OR SERVICE PROVIDERS BE LIABLE FOR ANY INDIRECT, CONSEQUENTIAL, EXEMPLARY, INCIDENTAL, SPECIAL OR PUNITIVE DAMAGES, INCLUDING, WITHOUT LIMITATION, LOSS OF PROFITS, WHETHER INCURRED DIRECTLY OR INDIRECTLY, OR ANY LOSS OF DATA, USE, GOODWILL, OR OTHER INTANGIBLE LOSSES, RESULTING FROM: (I) YOUR ACCESS TO OR USE OF OR INABILITY TO ACCESS OR USE THE SERVICE, (II) THE CONDUCT OR CONTENT OF OTHER MEMBERS OR THIRD PARTIES ON, THROUGH, OR FOLLOWING USE OF THE SERVICE; OR (III) UNAUTHORIZED ACCESS, USE OR ALTERATION OF YOUR CONTENT, EVEN IF TINDER HAS BEEN ADVISED OF THE POSSIBILITY OF SUCH DAMAGES. IN NO EVENT WILL TINDER’S AGGREGATE LIABILITY TO YOU FOR ALL CLAIMS RELATING TO THE SERVICE EXCEED THE GREATER OF THE AMOUNT PAID, IF ANY, BY YOU TO TINDER FOR THE SERVICE AND USD100 WHILE YOU HAVE AN ACCOUNT.
SOME JURISDICTIONS DO NOT ALLOW THE EXCLUSION OR LIMITATION OF CERTAIN DAMAGES, SO SOME OR ALL OF THE EXCLUSIONS AND LIMITATIONS IN THIS SECTION MAY NOT APPLY TO YOU.

15. Arbitration, Class-Action Waiver, and Jury Waiver.
Except for members residing within the EU or European Economic Area and elsewhere where prohibited by applicable law:
1.	The exclusive means of resolving any dispute or claim arising out of or relating to this Agreement (including any alleged breach thereof) or the Service shall be BINDING ARBITRATION administered by JAMS under the JAMS Streamlined Arbitration Rules & Procedures, except as modified by our Arbitration Procedures. The one exception to the exclusivity of arbitration is that either party has the right to bring an individual claim against the other in a small claims court of competent jurisdiction, or, if filed in arbitration, the responding party may request that the dispute proceed in small claims court instead if the claim is within the jurisdiction of the small claims court. If the request to proceed in small claims court is made before an arbitrator has been appointed, the arbitration shall be administratively closed. If the request to proceed in small claims court is made after an arbitrator has been appointed, the arbitrator shall determine whether the dispute should remain in arbitration or instead be decided in small claims court. Such arbitration shall be conducted by written submissions only, unless either you or Tinder elect to invoke the right to an oral hearing before the Arbitrator. But whether you choose arbitration or small claims court, you agree that you will not under any circumstances commence, or maintain, or participate in against the Company any class action, class arbitration, or other representative action or proceeding against Tinder.
2.	By using the Service in any manner, you agree to the above arbitration agreement. In doing so, YOU GIVE UP YOUR RIGHT TO GO TO COURT to assert or defend any claims between you and the Company (except for matters that may be taken to small-claims court). YOU ALSO GIVE UP YOUR RIGHT TO PARTICIPATE IN A CLASS ACTION OR OTHER CLASS PROCEEDING. If you assert a claim against Tinder outside of small claims court (and Tinder does not request that the claim be moved to small claims court), your rights will be determined by a NEUTRAL ARBITRATOR, NOT A JUDGE OR JURY, and the arbitrator shall determine all claims and all issues regarding the arbitrability of the dispute. You are entitled to a fair hearing before the arbitrator. The arbitrator can generally grant any relief that a court can, including the ability to hear a dispositive motion (which may include a dispositive motion based upon the parties’ pleadings, as well as a dispositive motion based upon the parties’ pleadings along with the evidence submitted), but you should note that arbitration proceedings are usually simpler and more streamlined than trials and other judicial proceedings. Decisions by the arbitrator are enforceable in court and may be overturned by a court only for very limited reasons. For details on the arbitration process, see our Arbitration Procedures.
3.	Any proceeding to enforce this arbitration agreement, including any proceeding to confirm, modify, or vacate an arbitration award, may be commenced in any court of competent jurisdiction. In the event that this arbitration agreement is for any reason held to be unenforceable, any litigation against the Company (except for small-claims court actions) may be commenced only in the federal or state courts located in Dallas County, Texas. You hereby irrevocably consent to the jurisdiction of those courts for such purposes.
4.	The online dispute settlement platform of the European Commission is available under http://ec.europa.eu/odr. Tinder does not take part in dispute settlement procedures in front of a consumer arbitration entity for members residing in the EU or European Economic Area.

16. Governing Law.
For members residing in the EU or European Economic Area or elsewhere where our arbitration agreement is prohibited by law, the laws of Texas, U.S.A., excluding Texas’s conflict of laws rules, will apply to any disputes arising out of or relating to this Agreement or the Service. Notwithstanding the foregoing, the Arbitration Agreement in Section 15 above shall be governed by the Federal Arbitration Act. For the avoidance of doubt, the choice of Texas governing law shall not supersede any mandatory consumer protection legislation in such jurisdictions.

17. Venue.
Except for members residing in the EU or European Economic Area who may bring claims in their country of residence in accordance with applicable law and except for claims that may be properly brought in a small claims court of competent jurisdiction, all claims arising out of or relating to this Agreement, to the Service, or to your relationship with Tinder that for whatever reason are not submitted to arbitration will be litigated exclusively in the federal or state courts of Dallas County, Texas, U.S.A. You and Tinder consent to the exercise of personal jurisdiction of courts in the State of Texas and waive any claim that such courts constitute an inconvenient forum.

18. Indemnity by You.
You agree, to the extent permitted under applicable law, to indemnify, defend and hold harmless Tinder, our affiliates, and their and our respective officers, directors, agents, and employees from and against any and all complaints, demands, claims, damages, losses, costs, liabilities and expenses, including attorney’s fees due to, arising out of, or relating in any way to your access to or use of the Service, your Content, or your breach of this Agreement.

19. Entire Agreement; Other.
This Agreement, along with the Privacy Policy, Cookie Policy, the Safety Tips, the Arbitration Procedures (if applicable to you), and any terms disclosed and agreed to by you if you purchase additional features, products or services we offer on the Service, contains the entire agreement between you and Tinder regarding the use of the Service. If any provision of this Agreement is held invalid, the remainder of this Agreement shall continue in full force and effect. The failure of the Company to exercise or enforce any right or provision of this Agreement shall not constitute a waiver of such right or provision. You agree that your Tinder account is non-transferable and all of your rights to your account and its Content terminate upon your death. No agency, partnership, joint venture, fiduciary or other special relationship or employment is created as a result of this Agreement and you may not make any representations on behalf of or bind Tinder in any manner.
Summary of Terms
You must read and agree to our Terms of Use (the “Agreement”) because it forms the binding contract between you and Tinder. However, we’ve provided this short summary for your convenience (with capitalized terms defined in the Agreement).
Acceptance. By using the Service, you agree to this Agreement, our Privacy Policy and Safety Tips, and any terms that apply to in app purchase you make.
Eligibility. You must be at least 18 to use the Service and not prohibited by law from using the Service. You promise to follow the law and that you’re not a convicted felon or sex offender.
Your Account. If you use Facebook to access Tinder, you must authorize us to access certain information from Facebook to use Tinder. You agree to keep your account secure and confidential.
Modifying the Services; Termination. We may modify the Services at any time or discontinue them altogether. You can terminate your account in Settings, and we reserve the right to terminate it for you if you violate this Agreement or if we deem your use of the Service to be inappropriate.
Safety. Tinder is not responsible for the actions of its users or your interactions with them, and we don’t conduct background checks. Be careful and be sure to read and follow our Safety Tips on interacting with people on or off of Tinder.
Rights. Tinder grants you the right to use our Service as authorized and permitted by this Agreement. See the Agreement for a full list of actions you agree not to take. You grant Tinder the right to display your profile and Content for the limited purpose of Tinder operating the Service and researching and developing new ones.
Rules. See the Agreement for a list of the rules you agree to abide by when you use Tinder, such as not soliciting money from other users, harassing other users or using the Service for any illegal purposes.
In App Purchases. Tinder may offer services for purchase through mobile platforms such as iTunes and Google Play. Those purchases are governed by the terms of the platforms. Most purchases are not refundable and certain services only grant you a specified license, as further described in the Agreement.
Copyright. If you believe your work has been posted on the Service in violation of your copyright, please send a notice to our Copyright Agent following instructions in the Agreement.
Disclaimers. Our Service is provided “as is,” and we disclaim legal liability for the quality, safety, or reliability of our Service.
Limitation of Liability. To the extent allowed by law, we limit our liability to you for certain types of damages for claims relating to your use of the Service, the conduct of other users and unauthorized access or use of your Content. Our aggregate liability will not exceed the fees you pay us.
Arbitration. Our Agreement contains a mandatory arbitration clause. You agree that any disputes will be settled by arbitration, and you waive your right to a trial by jury or to participate in a class action. This does not apply to users residing in the EU, Norway or elsewhere where prohibited by law.
Governing Law; Venue. Texas law applies to this Agreement. Actions for disputes relating to this Agreement must be brought in Dallas, Texas, except for users residing in the EU or Norway, who may bring claims in their country of residence.
Indemnity. You agree to indemnify us for actions arising out of your use of the Service, your Content or your violation of this Agreement.
`}
          </Typography>

          {/* PRIVACY POLICY */}
          <Typography variant="h5" gutterBottom sx={{ mt: 6, fontWeight: 700, color: primaryColor }}>
            Privacy Policy
          </Typography>
          <Typography variant="caption" color="text.secondary" paragraph>
            Effective Date: April 21st, 2021
          </Typography>

          <Typography variant="body2" paragraph sx={{ whiteSpace: 'pre-wrap' }}>
            {`Welcome to Boudou’s Privacy Policy. 
            Thank you for taking the time to read it.
We appreciate that you trust us with your information and we intend to always keep that trust. 
This starts with making sure you understand the information we collect, why we collect it, how it is used and your choices regarding your information. This 1qq describes our privacy practices in plain language, keeping legal and technical jargon to a minimum.
This Privacy Policy applies from 21st, 2021. The previous version of this Privacy Policy, will be available here, will apply until then.
EFFECTIVE DATE: April 21st, 2021


1. Who We Are
BOULDOU.SC PTY LTD
Anse Etoile
Mahe
Seychelles

2. Where This Privacy Policy Applies
This Privacy Policy applies to websites, apps, events and other services operated by Bouldou. For simplicity, we refer to all of these as our “services” in this Privacy Policy. To make it extra clear, we’ve added links to this Privacy Policy on all applicable services.
Some services may require their own unique privacy policy. If a particular service has its own privacy policy, then that policy -- not this Privacy Policy -- applies.

3. Information We Collect
It goes without saying, we can’t help you develop meaningful connections without some information about you, such as basic profile details and the types of people you’d like to meet. We also collect information generated as you use our services, for example access logs, as well as information from third parties, like when you access our services through a social media account. If you want additional info, we go into more detail below.

4. Information you give us
You choose to give us certain information when using our services. This includes:
1. When you create an account, you provide us with at least your login credentials, as well as some basic details necessary for the service to work, such as your gender and date of birth. The photo verification process is one that the Administration has to approve prior to consent to being a user. Through this process, we guarantee that Bouldou.sc remains a Seychellois only dating website; that we limit on fake accounts; that we ensure that 18year olds and above solely are registering and that a certain level of control is established to avoid predators. The information given during this registration remains between Administration and user only.
2. When you complete your profile, you can share with us additional information, such as details on your personality, lifestyle, interests and other details about you, as well as content such as photos and videos. To add certain content, like pictures or videos, you may allow us to access your camera or photo album. Some of the information you choose to provide us may be considered “special” or “sensitive” in certain jurisdictions, for example your racial or ethnic origins, sexual orientation and religious beliefs. By choosing to provide this information, you consent to our processing of that information.
3. When you subscribe to a paid service or make a purchase directly from us (rather than through a platform such as iOS or Android), you provide us or our payment service provider with information, such as your debit or credit card number or other financial information.
4. When you participate in surveys or focus groups, you give us your insights into our products and services, responses to our questions and testimonials.
5. When you choose to participate in our promotions, events or contests, we collect the information that you use to register or enter.
6. If you contact our customer care team, we collect the information you give us during the interaction. Sometimes, we monitor or record these interactions for training purposes and to ensure a high quality of service.
7. If you ask us to communicate with or otherwise process information of other people (for example, if you ask us to send an email on your behalf to one of your friends), we collect the information about others that you give us in order to complete your request.
8.Of course, we also process your chats with other users as well as the content you publish, as part of the operation of the services.

5. Information we receive from others
In addition to the information you provide us directly, we receive information about you from others, including:
Other Users
Other users may provide information about you as they use our services. For instance, we may collect information about you from other users if they contact us about you.
Social Media
You may be able to use your social media login (such as Facebook Login) to create and log into your Bouldou account. This saves you from having to remember yet another user name and password and allows you to share some information from your social media account with us.
Other Partners
We may receive info about you from our partners, for instance where Bouldou ads are published on a partner’s websites and platforms (in which case they may pass along details on a campaign’s success).

6. Information collected when you use our services
When you use our services, we collect information about which features you’ve used, how you’ve used them and the devices you use to access our services. See below for more details:
Usage Information.  We collect information about your activity on our services, for instance how you use them (e.g., date and time you logged in, features you’ve been using, searches, clicks and pages which have been shown to you, referring webpage address, advertising that you click on) and how you interact with other users (e.g., users you connect and interact with, time and date of your exchanges, number of messages you send and receive).
Device information.  We collect information from and about the device(s) you use to access our services, including:
 i) hardware and software information such as IP address, device ID and type, device-specific and apps settings and characteristics, app crashes, advertising IDs (such as Google’s AAID and Apple's IDFA, both of which are randomly generated numbers that you can reset by going into your device’ settings), browser type, version and language, operating system, time zones, identifiers associated with cookies or other technologies that may uniquely identify your device or browser (e.g., IMEI/UDID and MAC address);
ii) information on your wireless and mobile network connection, like your service provider and signal strength;
iii)  information on device sensors such as accelerometers, gyroscopes and compasses.
Other information with your consent If you give us permission, we can collect your precise geolocation (latitude and longitude) through various means, depending on the service and device you’re using, including GPS, Bluetooth or Wi-Fi connections. The collection of your geolocation may occur in the background even when you aren’t using the services if the permission you gave us expressly permits such collection. If you decline permission for us to collect your geolocation, we will not collect it. Similarly, if you consent, we may collect your photos and videos (for instance, if you want to publish a photo, video or streaming on the services).

7. Cookies and Other Similar Data Collection Technologies
We use and may allow others to use cookies and similar technologies (e.g., web beacons, pixels) to recognize you and/or your device(s). Cookies serve the purposes such as authenticating you, remembering your preferences and settings, analyzing site traffic and trends, delivering and measuring the effectiveness of advertising campaigns, allowing you to use social features and how you can better control their use, through your browser settings and other tools.
Some web browsers (including Safari, Internet Explorer, Firefox and Chrome) have a “Do Not Track” (“DNT”) feature that tells a website that a user does not want to have his or her online activity tracked. If a website that responds to a DNT signal receives a DNT signal, the browser can block that website from collecting certain information about the browser’s user. Not all browsers offer a DNT option and DNT signals are not yet uniform. For this reason, many businesses, including Tinder, do not currently respond to DNT signals.

8. How We Use Information
The main reason we use your information is to deliver and improve our services. Additionally, we use your info to help keep you safe and to provide you with advertising that may be of interest to you. Read on for a more detailed explanation of the various reasons we use your information, together with practical examples.
To administer your account and provide our services to you
1. Create and manage your account
2. you with customer support and respond to your requests
3. Complete your transactions
4. Communicate with you about our services, including order management and billing

To help you connect with other users
1.Analyze your profile, activity on the service, and preferences to recommend meaningful connections to you and recommend you to others.
2. Show users’ profiles to one another

To ensure a consistent experience across your devices
Link the various devices you use so that you can enjoy a consistent experience of our services on all of them. We do this by linking devices and browser data, such as when you log into your account on different devices or by using partial or full IP address, browser version and similar data about your devices to help identify and link them.

To provide new Bouldou services to you
1. Register you and display your profile on new Bouldou features and apps
2. Administer your account on these new features and apps

To serve you relevant offers and ads
1. Administer sweepstakes, contests, discounts or other offers
2. Develop, display and track content and advertising tailored to your interests on our services and other sites
3. Communicate with you by email, phone, social media or mobile device about products or services that we think may interest you.

To improve our services and develop new ones
1. Administer focus groups and surveys
2. Conduct research and analysis of users’ behavior to improve our services and content (for instance, we may decide to change the look and feel or even substantially modify a given feature based on users’ behavior)
3.Develop new features and services (for example, we may decide to build a new interests-based feature further to requests received from users).

To prevent, detect and fight fraud or other illegal or unauthorized activities
1. Address ongoing or alleged misbehavior on and off-platform
2. Perform data analysis to better understand and design countermeasures against these activities
3. Retain data related to fraudulent activities to prevent against recurrences
To ensure legal compliance
1. Comply with legal requirements
2. Assist law enforcement
3. Enforce or exercise our rights, for example our Terms

To process your information as described above, we rely on the following legal bases:
Provide our service to you: Most of the time, the reason we process your information is to perform the contract that you have with us. For instance, as you go about using our service to build meaningful connections, we use your information to maintain your account and your profile, to make it viewable to other users and recommend other users to you.
Legitimate interests: We may use your information where we have legitimate interests to do so. For instance, we analyze users’ behavior on our services to continuously improve our offerings, we suggest offers we think might interest you, and we process information for administrative, fraud detection and other legal purposes.
Consent: From time to time, we may ask for your consent to use your information for certain specific reasons. You may withdraw your consent at any time by contacting us at the address provided at the end of this Privacy Policy.

9. How We Share Information
Since our goal is to help you make meaningful connections, the main sharing of users’ information is, of course, with other users. We also share some users’ information with service providers and partners who assist us in operating the services, with other Match Group companies and, in some cases, legal authorities. Read on for more details about how your information is shared with others.
With other users
You share information with other users when you voluntarily disclose information on the service (including your public profile). Please be careful with your information and make sure that the content you share is stuff that you’re comfortable being publically viewable since neither you nor we can control what others do with your information once you share it.
If you choose to limit the audience for all or part of your profile or for certain content or information about you, then it will be visible according to your settings.
With our service providers and partners
We use third parties to help us operate and improve our services. These third parties assist us with various tasks, including data hosting and maintenance, analytics, customer care, marketing, advertising, payment processing and security operations.
We may also share information with partners who distribute and assist us in advertising our services. For instance, we may share limited information on you in hashed, non-human readable form to advertising partners.
We follow a strict vetting process prior to engaging any service provider or working with any partner. All of our service providers and partners must agree to strict confidentiality obligations.
For corporate transactions
We may transfer your information if we are involved, whether in whole or in part, in a merger, sale, acquisition, divestiture, restructuring, reorganization, dissolution, bankruptcy or other change of ownership or control.
When required by law
We may disclose your information if reasonably necessary: (i) to comply with a legal process, such as a court order, subpoena or search warrant, government / law enforcement investigation or other legal requirements; (ii) to assist in the prevention or detection of crime (subject in each case to applicable law); or (iii) to protect the safety of any person.
To enforce legal rights
We may also share information: (i) if disclosure would mitigate our liability in an actual or threatened lawsuit; (ii) as necessary to protect our legal rights and legal rights of our users, business partners or other interested parties; (iii) to enforce our agreements with you; and (iv) to investigate, prevent, or take other action regarding illegal activity, suspected fraud or other wrongdoing.
With your consent or at your request
We may ask for your consent to share your information with third parties. In any such case, we will make it clear why we want to share the information.
We may use and share non-personal information (meaning information that, by itself, does not identify who you are such as device information, general demographics, general behavioral data, geolocation in de-identified form), as well as personal information in hashed, non-human readable form, under any of the above circumstances. We may also share this information with other Match Group companies and third parties (notably advertisers) to develop and deliver targeted advertising on our services and on websites or applications of third parties, and to analyze and report on advertising you see. We may combine this information with additional non-personal information or personal information in hashed, non-human readable form collected from other sources.

10. Your Rights
We want you to be in control of your information, so we have provided you with the following tools:
Access / Update tools in the service. Tools and account settings that help you to access, rectify or delete information that you provided to us and that’s associated with your account directly within the service. If you have any question on those tools and settings, please contact us.
Device permissions. Mobile platforms have permission systems for specific types of device data and notifications, such as phone book and location services as well as push notifications. You can change your settings on your device to either consent or oppose the collection of the corresponding information or the display of the corresponding notifications. Of course, if you do that, certain services may lose full functionality.
Deletion. You can delete your account by using the corresponding functionality directly on the service.
For your protection and the protection of all of our users, we may ask you to provide proof of identity before we can answer the above requests.
Keep in mind, we may reject requests for certain reasons, including if the request is unlawful or if it may infringe on trade secrets or intellectual property or the privacy of another user. If you wish to receive information relating to another user, such as a copy of any messages you received from him or her through our service, the other user will have to contact our Privacy Officer to provide their written consent before the information is released.
Also, we may not be able to accommodate certain requests to object to the processing of personal information, notably where such requests would not allow us to provide our service to you anymore. For instance, we cannot provide our service if we do not have your date of birth.

11. How We Protect Your Information
We work hard to protect you from unauthorized access to or alteration, disclosure or destruction of your personal information. As with all technology companies, although we take steps to secure your information, we do not promise, and you should not expect, that your personal information will always remain secure.
We regularly monitor our systems for possible vulnerabilities and attacks and regularly review our information collection, storage and processing practices to update our physical, technical and organizational security measures.
We may suspend your use of all or part of the services without notice if we suspect or detect any breach of security. If you believe that your account or information is no longer secure, please notify us immediately.

12. How Long We Retain Your Information
We keep your personal information only as long as we need it for legitimate business purposes and as permitted by applicable law. To protect the safety and security of our users on and off our services, we implement a safety retention window of three months following account deletion. During this period, account information will be retained although the account will of course not be visible on the services anymore.
In practice, we delete or anonymize your information upon deletion of your account (following the safety retention window) or after two years of continuous inactivity, unless:
We must keep it to comply with applicable law (for instance, some “traffic data” is kept for one year to comply with statutory data retention obligations);</p>
We must keep it to evidence our compliance with applicable law (for instance, records of consents to our Terms, Privacy Policy and other similar consents are kept for five years);
There is an outstanding issue, claim or dispute requiring us to keep the relevant information until it is resolved; or
Information must be kept for our legitimate business interests, such as fraud prevention and enhancing users' safety and security. For example, information may need to be kept to prevent a user who was banned for unsafe behavior or security incidents from opening a new account.
Keep in mind that even though our systems are designed to carry out data deletion processes according to the above guidelines, we cannot promise that all data will be deleted within a specific timeframe due to technical constraints.

13. Children's Privacy
Our services are restricted to users who are 18 years of age or older. We do not permit users under the age of 18 on our platform and we do not knowingly collect personal information from anyone under the age of 18. If you suspect that a user is under the age of 18, please use the reporting mechanism available through the service.

14. Privacy Policy Changes
Because we’re always looking for new and innovative ways to help you build meaningful connections, this policy may change over time. We will notify you before any material changes take effect so that you have time to review the changes.
`}
          </Typography>

          {/* Extra spacer to force scrolling */}
          <Box sx={{ height: '180px' }} />
        </DialogContent>

        <DialogActions sx={{ px: 4, pb: 3, pt: 2 }}>
          <Button
            onClick={() => setOpenTerms(false)}
            sx={{ color: '#666', mr: 1 }}
          >
            Close
          </Button>
          <Button
            variant="contained"
            disabled={!hasReadTerms}
            onClick={() => {
              setHasReadTerms(true);
              setTermsAccepted(true);
              setOpenTerms(false);
            }}
            sx={{
              background: `linear-gradient(45deg, ${primaryColor}, ${secondaryColor})`,
              boxShadow: '0 4px 14px rgba(178,0,187,0.3)',
            }}
          >
            I have read and agree
          </Button>
        </DialogActions>
      </Dialog>

    </Box>
  );
};

export default SignUp;